import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ssoCallback } from '../services/auth';

/**
 * SSO Callback Page
 *
 * Google (or other OAuth provider) redirects here after the user grants consent.
 * URL will contain ?code=...&state=... query params.
 *
 * Two modes:
 * 1. Popup mode: This page is inside a popup window. We extract the code and
 *    let the opener's polling (openSsoPopup) pick it up, OR post a message.
 * 2. Redirect mode: This is the main window. We exchange the code ourselves
 *    and complete the login flow.
 *
 * We detect popup vs redirect by checking window.opener.
 */
export default function SsoCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [status, setStatus] = useState('Processing SSO login...');
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Google denied access: ${errorParam}`);
      return;
    }

    if (!code) {
      setError('No authorization code received from Google.');
      return;
    }

    // Determine provider from state or default to google
    const provider = sessionStorage.getItem('sso_provider') || 'google';

    // If we're in a popup, let the opener handle it
    if (window.opener && !window.opener.closed) {
      // Post message to parent window with the code
      try {
        window.opener.postMessage({
          type: 'SSO_CALLBACK',
          provider,
          code,
          state,
        }, window.location.origin);
      } catch (e) {
        // Cross-origin — the polling in openSsoPopup will pick up the code from URL
      }
      setStatus('SSO authentication received. This window will close...');
      setTimeout(() => window.close(), 1500);
      return;
    }

    // Redirect mode: exchange code with backend ourselves
    (async () => {
      try {
        setStatus('Exchanging authorization code...');
        const result = await ssoCallback(provider, code, state);

        if (!result.ok) {
          setError(result.error || 'SSO authentication failed');
          return;
        }

        if (result.mfaRequired) {
          // Redirect to login page with MFA view
          setStatus('MFA required. Redirecting...');
          navigate('/login?mfa=true', { replace: true });
          return;
        }

        // Success — the ssoCallback already stored tokens
        setStatus('Login successful! Redirecting...');
        // Force a page reload to pick up the new auth state
        window.location.href = '/';
      } catch (err) {
        console.error('[sso-callback] Error:', err);
        setError('SSO authentication failed: ' + err.message);
      }
    })();
  }, [searchParams, navigate, authLogin]);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#fafaf9',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e4e4e7',
        padding: '40px 48px', textAlign: 'center', maxWidth: 420,
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>
        {/* Logo */}
        <div style={{
          width: 56, height: 56, borderRadius: 12, background: '#e3f4f1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          border: '1px solid #a5d8d0',
          overflow: 'hidden',
        }}>
          <img src="/brand/mark-dark.png" alt="Prove7 Control Vector Platform" style={{ width: 44, height: 'auto' }}
            onError={(e) => { e.target.textContent = '🔐'; e.target.style.fontSize = '28px'; }}
          />
        </div>

        {error ? (
          <>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#dc2626', margin: '0 0 8px 0' }}>
              SSO Error
            </h2>
            <p style={{ fontSize: 13, color: '#71717a', lineHeight: 1.5, margin: '0 0 20px 0' }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              style={{
                padding: '8px 24px', borderRadius: 6, border: '1px solid #e4e4e7',
                background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                color: '#52525b',
              }}
            >
              Back to Login
            </button>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', margin: '0 0 8px 0' }}>
              Authenticating...
            </h2>
            <p style={{ fontSize: 13, color: '#71717a', lineHeight: 1.5 }}>
              {status}
            </p>
            {/* Spinner */}
            <div style={{
              width: 32, height: 32, border: '3px solid #e4e4e7',
              borderTopColor: '#4285F4', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '20px auto 0',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}
      </div>
    </div>
  );
}
